import type { NodePath } from '@/components/tree-dnd/tree-dnd-types';

const ITEM_PREFIX = 'item:';
const CONTAINER_PREFIX = 'container:';
const INSERT_PREFIX = 'insert:';

export function encodePath(path: NodePath): string {
  return path.join('.');
}

export function decodePath(encoded: string): NodePath {
  if (!encoded) return [];
  return encoded.split('.').map((segment) => Number(segment));
}

export function encodeItemId(path: NodePath): string {
  return `${ITEM_PREFIX}${encodePath(path)}`;
}

export function encodeContainerId(path: NodePath): string {
  return `${CONTAINER_PREFIX}${encodePath(path)}`;
}

export function parseItemId(id: string | number): NodePath | null {
  if (typeof id !== 'string' || !id.startsWith(ITEM_PREFIX)) return null;
  return decodePath(id.slice(ITEM_PREFIX.length));
}

export function parseContainerId(id: string | number): NodePath | null {
  if (typeof id !== 'string' || !id.startsWith(CONTAINER_PREFIX)) return null;
  return decodePath(id.slice(CONTAINER_PREFIX.length));
}

export function encodeInsertId(parentPath: NodePath, index: number): string {
  return `${INSERT_PREFIX}${encodePath(parentPath)}/${index}`;
}

export function parseInsertId(
  id: string | number
): { parentPath: NodePath; index: number } | null {
  if (typeof id !== 'string' || !id.startsWith(INSERT_PREFIX)) return null;

  const body = id.slice(INSERT_PREFIX.length);
  const separatorIndex = body.lastIndexOf('/');
  if (separatorIndex === -1) return null;

  const index = Number(body.slice(separatorIndex + 1));
  if (Number.isNaN(index)) return null;

  return {
    parentPath: decodePath(body.slice(0, separatorIndex)),
    index,
  };
}

export function pathsEqual(a: NodePath, b: NodePath): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

/** True when `path` is the same as or nested under `ancestor`. */
export function isDescendantOrEqual(
  ancestor: NodePath,
  path: NodePath
): boolean {
  if (path.length < ancestor.length) return false;
  return ancestor.every((value, index) => path[index] === value);
}

export function isInvalidMove(
  sourcePath: NodePath,
  destinationParentPath: NodePath
): boolean {
  if (sourcePath.length === 0) return true;
  return isDescendantOrEqual(sourcePath, destinationParentPath);
}

/** True when the item would end up in the same position after the move. */
export function isNoOpMove(
  sourcePath: NodePath,
  destinationParentPath: NodePath,
  destinationIndex: number
): boolean {
  const sourceParentPath = sourcePath.slice(0, -1);
  const sourceIndex = sourcePath[sourcePath.length - 1];

  if (!pathsEqual(sourceParentPath, destinationParentPath)) {
    return false;
  }

  let insertIndex = destinationIndex;
  if (sourceIndex < insertIndex) {
    insertIndex -= 1;
  }

  return insertIndex === sourceIndex;
}

export function remapPathAfterRemoval(
  removedPath: NodePath,
  targetPath: NodePath
): NodePath {
  const result = [...targetPath];
  const removedParent = removedPath.slice(0, -1);
  const removedIndex = removedPath[removedPath.length - 1];

  for (let depth = 0; depth < result.length; depth++) {
    const prefix = result.slice(0, depth + 1);
    const parentPrefix = prefix.slice(0, -1);
    const indexAtDepth = prefix[prefix.length - 1];

    if (
      pathsEqual(parentPrefix, removedParent) &&
      removedIndex < indexAtDepth
    ) {
      result[depth] -= 1;
    }
  }

  return result;
}
